import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { type ReactElement, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useDeleteBuild, useUpdateBuild } from '@/features/builds/hooks/builds';
import { buildJobDetailPath, buildJobRunDetailPath } from '@/features/jobs/utils/jobPaths';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { buildStageDetailPath, buildSyntheticStageId } from '@/features/pipelines/utils/stagePaths';
import {
  useProjectFilter,
  useProjectMetricThresholds,
  useProjectsQuery,
} from '@/features/projects';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { getBuildStatusIconComponent } from '@/features/shared/components/tableSystem/buildStatusIcons';
import {
  TableActionsCell,
  TableCellContainer,
} from '@/features/shared/components/tableSystem/TableCells';
import {
  TABLE_ROW_HEIGHT,
  TABLE_ROW_MEASURE_HEIGHT,
} from '@/features/shared/components/tableSystem/tableConstants';
import {
  resolveLatestChipBackground,
  resolveLatestChipForeground,
} from '@/features/shared/components/tableSystem/TableLatestCellStyles';
import {
  CLONE_META_DETAIL_GLYPH_SX,
  TableStatusMetaContent,
} from '@/features/shared/components/tableSystem/TableMetaCells';
import { TableMetricsBreakdownCell } from '@/features/shared/components/tableSystem/TableMetricsBreakdownCell';
import { TableStageStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStageStatusBreakdownCell';
import { tableSx } from '@/features/shared/components/tableSystem/tableSx';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { buildTagColorByName, resolveTagColor } from '@/features/shared/tags';
import { mapChipKeyToStatusTone } from '@/features/shared/utils/tableMetricPresentation';
import { useNotifications } from '@/providers/notificationHooks';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getChartTitle } from '@/shared/copy/charts';
import { getDetailLabel } from '@/shared/copy/details';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { getStatusCopy } from '@/shared/copy/status';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { queryKeys, useTagOptions } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import {
  Box,
  Chip,
  formatDuration,
  formatFrequency,
  formatPercent,
  formatStatusLabel,
  Inline,
} from '@/ui';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { getThresholdChipKey } from '@/ui/composites/table';
import { linkTruncateSx } from '@/ui/composites/table/cellContents/shared';
import {
  tableCellPrimaryTextSx,
  tableCellSecondaryTextSx,
  tableCellTagChipSx,
  tableCellTagGap,
  tableCellTagRowSx,
  tableCellTextBlockSx,
} from '@/ui/composites/table/cells/sharedCellContent';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { Link, resolveStatusChipTone, StagesIcon, Text, type GridColDef } from '@/ui/primitives';
import { EventOutlinedIcon, TimerOutlinedIcon } from '@/ui/primitives/icons';
import type { TableQueryMeta } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import type { TagColor } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { GridColumnVisibilityModel } from '@/ui/primitives';
import type { JobListItemDto, MetricThresholdGroup, QueryResult } from '@flowqa/shared';

type JobsTableProps = {
  queryFn: (input: GridQueryInput) => Promise<QueryResult<JobListItemDto>>;
  queryKey?: QueryKey;
  noRowsText?: string;
  search?: string;
  onQueryMeta?: (meta: TableQueryMeta<JobListItemDto> | null) => void;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (nextModel: GridColumnVisibilityModel) => void;
  tagOptionsOverride?: Array<{ value: string; label: string; color: TagColor }>;
  preservePreviousData?: boolean;
};

const buildJobsColumns = ({
  selectedProjectId,
  thresholds,
  resolveThresholds,
  tagColorByName = {},
  includeActionsColumn = false,
  onEditRow,
  onDeleteRow,
  editRowPendingId = null,
  deleteRowPendingId = null,
}: {
  selectedProjectId: string | null;
  thresholds: MetricThresholdGroup;
  resolveThresholds: (row: { projectId: string }) => MetricThresholdGroup | undefined;
  tagColorByName?: Partial<Record<string, TagColor>>;
  includeActionsColumn?: boolean;
  onEditRow?: (row: JobListItemDto) => void;
  onDeleteRow?: (row: JobListItemDto) => void;
  editRowPendingId?: string | null;
  deleteRowPendingId?: string | null;
}): Array<GridColDef<JobListItemDto>> => {
  const resolvedThresholds = (row: JobListItemDto): MetricThresholdGroup => {
    if (selectedProjectId) {
      return thresholds;
    }

    return resolveThresholds(row) ?? thresholds;
  };

  const nameColumn: GridColDef<JobListItemDto> = {
    field: 'jobName',
    headerName: 'Job',
    flex: 1.2,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-name-cell',
    renderCell: (params): ReactElement => {
      const pipelineHref =
        params.row.pipelineId && params.row.pipelineName
          ? `/pipelines/${resolvePipelineSlug({
              id: params.row.pipelineId,
              name: params.row.pipelineName,
            })}`
          : null;
      const stageHref = params.row.stageName
        ? buildStageDetailPath(buildSyntheticStageId(params.row.pipelineId, params.row.stageName))
        : null;

      return (
        <TableCellContainer gap="none">
          <Box sx={tableCellTextBlockSx}>
            <Text role="body" dataGridTypography="custom" sx={tableCellPrimaryTextSx}>
              <Link
                component={RouterLink}
                to={buildJobDetailPath(params.row.id)}
                data-grid-typography="custom"
                underline="hover"
                color="inherit"
                onClick={(event) => {
                  event.stopPropagation();
                }}
                sx={linkTruncateSx}
              >
                {params.row.jobName}
              </Link>
            </Text>
            {params.row.pipelineName || params.row.stageName ? (
              <Text
                role="body"
                dataGridTypography="custom"
                color="textSecondary"
                sx={tableCellSecondaryTextSx}
              >
                {params.row.pipelineName ? (
                  pipelineHref ? (
                    <Link
                      component={RouterLink}
                      to={pipelineHref}
                      data-grid-typography="custom"
                      underline="hover"
                      color="inherit"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      {params.row.pipelineName}
                    </Link>
                  ) : (
                    params.row.pipelineName
                  )
                ) : null}
                {params.row.pipelineName && params.row.stageName ? ' / ' : null}
                {params.row.stageName ? (
                  stageHref ? (
                    <Link
                      component={RouterLink}
                      to={stageHref}
                      data-grid-typography="custom"
                      underline="hover"
                      color="inherit"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      {params.row.stageName}
                    </Link>
                  ) : (
                    params.row.stageName
                  )
                ) : null}
              </Text>
            ) : null}
            {(params.row.tags ?? []).length > 0 ? (
              <Box sx={[tableCellTagRowSx, { mt: s('sm') }]}>
                <Inline
                  gap={tableCellTagGap}
                  wrap="wrap"
                  align="center"
                  sx={{ width: '100%', minWidth: 0 }}
                >
                  {(params.row.tags ?? []).map((tag, index) => (
                    <Chip
                      key={`${params.row.id}-${tag}-${String(index)}`}
                      size="xs"
                      tone={resolveTagColor(tag, tagColorByName)}
                      label={tag}
                      sx={tableCellTagChipSx}
                    />
                  ))}
                </Inline>
              </Box>
            ) : null}
          </Box>
        </TableCellContainer>
      );
    },
  };

  const runsColumn: GridColDef<JobListItemDto> = {
    field: 'runTotal',
    headerName: 'Runs',
    flex: 0.55,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-breakdown-cell',
    filterable: false,
    sortable: false,
    renderCell: (params): ReactElement => (
      <TableCellContainer gap="none">
        <TableStageStatusBreakdownCell
          total={params.row.runTotal}
          counts={params.row.runStatusCounts}
          totalLabelSingular="run"
          totalLabelPlural="runs"
          showDetailLabel={false}
          totalIcon={StagesIcon}
          summaryItems={(params.row.runSummaries ?? []).map((run) => ({
            key: run.id,
            detailLabel: params.row.jobName,
            executionLabel: run.buildKey,
            href: buildJobDetailPath(params.row.id),
            executionHref: buildJobRunDetailPath({
              jobRunId: run.id,
              pipelineId: params.row.pipelineId,
              stageName: params.row.stageName,
              jobName: params.row.jobName,
            }),
            runNumber: null,
            status: run.status,
            timestamp: run.timestamp,
          }))}
        />
      </TableCellContainer>
    ),
  };

  const statusColumn: GridColDef<JobListItemDto> = {
    field: 'status',
    headerName: getChartTitle('latestStatus'),
    flex: 0.8,
    align: 'left',
    headerAlign: 'left',
    cellClassName: 'table-status-meta-cell',
    renderCell: (params): ReactElement => {
      const status = params.row.status;
      const statusTone = resolveStatusChipTone(status);
      const StatusIcon = getBuildStatusIconComponent(status);
      const durationLabel = formatDuration(params.row.latestRun?.durationMs ?? null);
      const timestamp = params.row.latestRun?.timestamp ?? params.row.lastUpdatedAt;
      const timestampLabel = timestamp ? formatRelativeTime(timestamp) : '';
      const timestampTitle = timestamp ? formatDate(timestamp, { includeTime: true }) : '';

      return (
        <TableCellContainer gap="none">
          <TableStatusMetaContent
            statusLabel={formatStatusLabel(status)}
            statusHref={
              params.row.latestRun
                ? buildJobRunDetailPath({
                    jobRunId: params.row.latestRun.id,
                    pipelineId: params.row.pipelineId,
                    stageName: params.row.stageName,
                    jobName: params.row.jobName,
                  })
                : null
            }
            chipTone={statusTone}
            chipIcon={<Box component={StatusIcon} />}
            chipBackgroundColor={(theme) => resolveLatestChipBackground(theme, statusTone)}
            chipForegroundColor={(theme) => resolveLatestChipForeground(theme, statusTone)}
            durationLabel={durationLabel}
            durationIcon={<TimerOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
            timestampLabel={timestampLabel}
            timestampTitle={timestampTitle}
            timestampIcon={<EventOutlinedIcon sx={CLONE_META_DETAIL_GLYPH_SX} />}
          />
        </TableCellContainer>
      );
    },
  };

  const metricsColumn: GridColDef<JobListItemDto> = {
    field: 'metricsSummary',
    headerName: getDetailLabel('metrics'),
    flex: 0.9,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    renderCell: (params): ReactElement => {
      const passRate = params.row.metrics?.passRate ?? null;
      const frequency = params.row.metrics?.frequencyPerDay ?? null;
      const flakeScore = params.row.metrics?.flakeScore ?? null;
      const avgDuration = params.row.metrics?.averageDurationMs ?? null;
      const avgDurationThresholdValue = avgDuration === null ? null : avgDuration / 1000;
      const thresholdGroup = resolvedThresholds(params.row);

      return (
        <TableCellContainer gap="none">
          <TableMetricsBreakdownCell
            items={[
              {
                key: 'passRate',
                label: getChartTitle('passRate'),
                valueLabel:
                  passRate === null ? getStatusCopy('noData') : `${formatPercent(passRate)}%`,
                deltaValue: null,
                isMissing: passRate === null,
                tone: mapChipKeyToStatusTone(
                  getThresholdChipKey(passRate, thresholdGroup.passRate),
                ),
                direction: thresholdGroup.passRate.direction,
              },
              {
                key: 'frequency',
                label: getChartTitle('frequency'),
                valueLabel:
                  frequency === null ? getStatusCopy('noData') : formatFrequency(frequency),
                deltaValue: null,
                isMissing: frequency === null,
                tone: mapChipKeyToStatusTone(
                  getThresholdChipKey(frequency, thresholdGroup.frequency),
                ),
                direction: thresholdGroup.frequency.direction,
              },
              {
                key: 'avgDuration',
                label: getChartTitle('avgDuration'),
                valueLabel:
                  avgDuration === null ? getStatusCopy('noData') : formatDuration(avgDuration),
                deltaValue: null,
                isMissing: avgDuration === null,
                tone: mapChipKeyToStatusTone(
                  getThresholdChipKey(avgDurationThresholdValue, thresholdGroup.avgDuration),
                ),
                direction: thresholdGroup.avgDuration.direction,
              },
              {
                key: 'flakeScore',
                label: getChartTitle('flakeScore'),
                valueLabel:
                  flakeScore === null ? getStatusCopy('noData') : `${formatPercent(flakeScore)}%`,
                deltaValue: null,
                isMissing: flakeScore === null,
                tone: mapChipKeyToStatusTone(
                  getThresholdChipKey(flakeScore, thresholdGroup.flakeScore),
                ),
                direction: thresholdGroup.flakeScore.direction,
              },
            ]}
          />
        </TableCellContainer>
      );
    },
  };

  const actionsColumn: GridColDef<JobListItemDto> = {
    field: 'actions',
    headerName: '',
    flex: 0.1,
    minWidth: 56,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params): ReactElement => (
      <TableActionsCell
        rowName={params.row.jobName}
        editActionLabel={getEntityActionLabel('edit', 'Job')}
        deleteActionLabel={getEntityActionLabel('delete', 'Job')}
        onEdit={params.row.latestRun ? () => onEditRow?.(params.row) : undefined}
        disableEdit={params.row.latestRun === null || editRowPendingId === params.row.id}
        onDelete={params.row.latestRun ? () => onDeleteRow?.(params.row) : undefined}
        disableDelete={params.row.latestRun === null || deleteRowPendingId === params.row.id}
        showEditTagsAction={false}
      />
    ),
  };

  return includeActionsColumn
    ? [nameColumn, statusColumn, runsColumn, metricsColumn, actionsColumn]
    : [nameColumn, statusColumn, runsColumn, metricsColumn];
};

export const JobsTable = ({
  queryFn,
  queryKey,
  noRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'jobRun' }),
  search,
  onQueryMeta,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  tagOptionsOverride,
  preservePreviousData = false,
}: JobsTableProps): ReactElement => {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const updateBuild = useUpdateBuild();
  const deleteBuild = useDeleteBuild();
  const [editTarget, setEditTarget] = useState<JobListItemDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<JobListItemDto | null>(null);
  const editEditor = useDetailEditEditor<JobListItemDto['status']>({
    projectId: editTarget?.projectId ?? null,
    successMessage: 'Job updated',
    saveEntity: async ({ tags, status, timestamp, durationMs }) => {
      const latestRunId = editTarget?.latestRun?.id ?? null;
      if (!latestRunId) return;
      const startedAt = timestamp ? timestamp.toISOString() : null;
      const finishedAt =
        timestamp && durationMs !== null && durationMs !== undefined
          ? new Date(timestamp.getTime() + durationMs).toISOString()
          : null;
      await updateBuild.mutateAsync({
        id: latestRunId,
        tags,
        status,
        startedAt,
        finishedAt,
      });
    },
  });
  const { tagOptions: fetchedTagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: tagOptionsOverride === undefined,
  });
  const tagOptions = tagOptionsOverride ?? fetchedTagOptions;
  const projectsQuery = useProjectsQuery();
  const thresholdsByProjectId = useMemo(() => {
    const entries = projectsQuery.data?.items ?? [];
    return new Map(entries.map((project) => [project.id, project.metricThresholds.jobs]));
  }, [projectsQuery.data?.items]);
  const resolveThresholds = useMemo(
    () =>
      (row: { projectId: string }): MetricThresholdGroup | undefined =>
        thresholdsByProjectId.get(row.projectId),
    [thresholdsByProjectId],
  );
  const tagColorByName = useMemo<Partial<Record<string, TagColor>>>(
    () => buildTagColorByName(tagOptions),
    [tagOptions],
  );
  const invalidateJobViews = async (jobId: string | null): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.jobs(selectedProjectId, selectedOrganisationId ?? null),
      }),
      ...(jobId
        ? [
            queryClient.invalidateQueries({
              queryKey: queryKeys.jobDetail(jobId, selectedOrganisationId ?? null),
            }),
          ]
        : []),
      queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'datagrid' &&
          typeof query.queryKey[1] === 'string' &&
          query.queryKey[1].startsWith('jobs'),
      }),
      queryClient.invalidateQueries({ queryKey: queryKeys.metricsPrefix() }),
    ]);
  };
  const columns = useMemo(
    () =>
      buildJobsColumns({
        selectedProjectId,
        thresholds: thresholds.jobs,
        resolveThresholds,
        tagColorByName,
        includeActionsColumn: true,
        onEditRow: (row) => {
          setEditTarget(row);
        },
        onDeleteRow: (row) => {
          setDeleteTarget(row);
        },
        editRowPendingId: editEditor.isPending ? (editTarget?.id ?? null) : null,
        deleteRowPendingId: deleteBuild.isPending ? (deleteTarget?.id ?? null) : null,
      }),
    [
      deleteBuild.isPending,
      deleteTarget?.id,
      editEditor.isPending,
      editTarget?.id,
      resolveThresholds,
      selectedProjectId,
      tagColorByName,
      thresholds.jobs,
    ],
  );
  const togglableColumnFields = useMemo(() => columns.map((column) => column.field), [columns]);
  const resolvedQueryKey =
    queryKey ??
    queryKeys.dataGrid('jobs/table', selectedOrganisationId ?? null, selectedProjectId ?? null);
  const tableGridProps = buildTableGridProps<JobListItemDto>({
    queryFn,
    queryKey: resolvedQueryKey,
    pageSizeOptions: [5, 10, 25, 50],
    initialPageSize: 5,
    noRowsText,
    showToolbar: false,
  });

  const editTargetStartedAt = editTarget?.latestRun?.timestamp ?? null;
  const editTargetDurationMs = editTarget?.latestRun?.durationMs ?? null;

  return (
    <>
      <ServerDataTable<JobListItemDto>
        queryPath="jobs/query"
        {...tableGridProps}
        columns={columns}
        getRowId={(row) => row.id}
        isAuthenticated={Boolean(session?.userId)}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
        search={search}
        onQueryMeta={onQueryMeta}
        columnVisibilityModel={columnVisibilityModel}
        interactionMode="static"
        getRowHeight={() => 'auto'}
        skeletonRowHeight={TABLE_ROW_HEIGHT}
        skeletonGetRowHeight={null}
        showSkeletonOnFetch={preservePreviousData}
        showPaginationSkeletonWhenRowsVisible={preservePreviousData}
        preservePreviousData={preservePreviousData}
        autoHeight
        gridProps={{
          getEstimatedRowHeight: () => TABLE_ROW_MEASURE_HEIGHT,
          onColumnVisibilityModelChange,
          slotProps: {
            columnsManagement: {
              disableShowHideToggle: true,
              disableResetButton: true,
              getTogglableColumns: () => togglableColumnFields,
            },
          },
        }}
        sx={tableSx}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Job"
        entityName={editTarget?.jobName ?? ''}
        projectId={editTarget?.projectId ?? null}
        tags={editTarget?.tags ?? []}
        status={editTarget?.status}
        statusOptions={[
          { value: 'queued', label: formatStatusLabel('queued') },
          { value: 'running', label: formatStatusLabel('running') },
          { value: 'completed', label: formatStatusLabel('completed') },
          { value: 'failed', label: formatStatusLabel('failed') },
        ]}
        timestamp={editTargetStartedAt}
        durationMs={editTargetDurationMs}
        timestampLabel="Started"
        durationLabel="Duration"
        durationInputMode="timeParts"
        loading={editEditor.isPending}
        cascadeCopy="Changes apply to the latest run shown for this job."
        onClose={() => {
          if (editEditor.isPending) return;
          setEditTarget(null);
        }}
        onSave={async ({ tags, colors, status, timestamp, durationMs }) => {
          try {
            await editEditor.save({ tags, colors, status, timestamp, durationMs });
            await invalidateJobViews(editTarget?.id ?? null);
            setEditTarget(null);
          } catch (error) {
            notify({ message: toErrorMessage(error), tone: 'red' });
          }
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'job' })}
        description="This deletes the latest run shown for this job."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'job' })}
        confirmColor="red"
        loading={deleteBuild.isPending}
        onClose={() => {
          if (deleteBuild.isPending) return;
          setDeleteTarget(null);
        }}
        onConfirm={() => {
          const latestRunId = deleteTarget?.latestRun?.id ?? null;
          if (!latestRunId || !deleteTarget) return;
          const deletedJobName = deleteTarget.jobName;
          deleteBuild.mutate(
            { id: latestRunId, projectId: deleteTarget.projectId },
            {
              onSuccess: async () => {
                await invalidateJobViews(deleteTarget.id);
                notify({
                  message: getToastMessage('entity.deleted', {
                    entity: 'job',
                    name: deletedJobName,
                  }),
                  tone: 'green',
                });
                setDeleteTarget(null);
              },
              onError: (error) => {
                notify({ message: toErrorMessage(error), tone: 'red' });
              },
            },
          );
        }}
      />
    </>
  );
};
